import type {ScreenSize} from './screen-size-constants'

/**
 * ScreenSizeStore provides a useSyncExternalStore-compatible interface
 * for tracking viewport size changes and mapping them to screen breakpoints.
 *
 * Uses ResizeObserver on document.documentElement to track viewport size changes.
 */
export class ScreenSizeStore {
  /**
   * Current screen size breakpoint.
   * `null` indicates the size hasn't been initialized yet.
   * Initialized lazily on first call to getSnapshot() or when observer starts.
   */
  #currentSize: ScreenSize | null = null
  #observer: ResizeObserver | null = null
  readonly #subscribers = new Set<() => void>()
  readonly #breakpoints: readonly ScreenSize[]
  readonly #defaultSize: ScreenSize

  constructor(breakpoints: readonly ScreenSize[], defaultSize: ScreenSize) {
    // Sort breakpoints descending for efficient lookup
    this.#breakpoints = [...breakpoints].sort((a, b) => b - a)
    this.#defaultSize = defaultSize
  }

  /**
   * Subscribe to viewport size changes.
   * Compatible with useSyncExternalStore's subscribe parameter.
   */
  subscribe = (notify: () => void): (() => void) => {
    // Subscribe should never be called during SSR
    if (typeof document === 'undefined') {
      throw new Error('ScreenSizeStore.subscribe() cannot be called on the server')
    }

    this.#subscribers.add(notify)
    this.#startObserving()

    return () => {
      this.#subscribers.delete(notify)
      this.#stopObserving()
    }
  }

  /**
   * Get the current screen size.
   * Compatible with useSyncExternalStore's getSnapshot parameter.
   */
  getSnapshot = (): ScreenSize => {
    // getSnapshot (not getServerSnapshot) should never be called during SSR
    if (typeof document === 'undefined') {
      throw new Error('ScreenSizeStore.getSnapshot() cannot be called on the server')
    }

    // Lazy-once initialization: compute size on first call
    if (this.#currentSize === null) {
      return this.#initializeCurrentSize()
    }

    return this.#currentSize
  }

  /**
   * Compute the screen size for a given width.
   * Public method for external use.
   */
  computeSize(width: number): ScreenSize {
    return this.#computeSize(width)
  }

  #handleResize = (entries: ResizeObserverEntry[]) => {
    const entry = entries[0]
    if (!entry) return

    // Use contentRect.width directly from observer - no separate DOM read
    const newSize = this.#computeSize(entry.contentRect.width)

    // Only notify if breakpoint actually changed
    // Initialize if null (can happen if resize fires before getSnapshot is called)
    if (this.#currentSize === null || newSize !== this.#currentSize) {
      this.#currentSize = newSize
      for (const callback of this.#subscribers) {
        callback()
      }
    }
  }

  #computeSize(width: number): ScreenSize {
    for (const breakpoint of this.#breakpoints) {
      if (width >= breakpoint) {
        return breakpoint
      }
    }
    return this.#defaultSize
  }

  #initializeCurrentSize(): ScreenSize {
    // Defensive checks for DOM availability
    if (typeof document === 'undefined' || !document.documentElement) {
      this.#currentSize = this.#defaultSize
      return this.#defaultSize
    }

    // Use clientWidth with fallback to innerWidth for mobile hydration race condition
    // On mobile browsers, clientWidth can be 0 before layout completes
    const clientWidth = document.documentElement.clientWidth
    let width = clientWidth

    // Fallback to window.innerWidth if clientWidth is 0
    if (width === 0 && typeof window !== 'undefined' && typeof window.innerWidth === 'number') {
      width = window.innerWidth
    }

    const size = this.#computeSize(width)
    this.#currentSize = size
    return size
  }

  #startObserving() {
    if (!this.#observer) {
      this.#observer = new ResizeObserver(this.#handleResize)
      this.#observer.observe(document.documentElement)
      // Belt and suspenders: Initialize current size if still null
      // getSnapshot() should have already initialized it, but be defensive
      if (this.#currentSize === null) {
        this.#initializeCurrentSize()
      }
    }
  }

  #stopObserving() {
    if (this.#subscribers.size === 0 && this.#observer) {
      this.#observer.disconnect()
      this.#observer = null
    }
  }
}
